import React, { useState, useMemo } from 'react';
import { 
  AlertTriangle, Bell, Filter, Clock, Activity, Search, 
  CheckCircle, XCircle, Zap, User, MapPin, Pill, Shield,
  TrendingUp, Download, RefreshCw, Eye, MoreVertical,
  AlertCircle, Info, X, CheckSquare, FileText, Package
} from 'lucide-react';
import { useAppContext, useAppActions } from '../../context/AppContext';
import { getFilteredAlerts, formatDate, formatTime } from '../../utils/helpers';
import { useToast } from '../Common/ToastProvider';

const Alerts = () => {
  const { state } = useAppContext();
  const { showToast } = useToast();
  const actions = useAppActions();
  const { selectedFacility, alertFilter, currentUser } = state;

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  const allAlerts = getFilteredAlerts(selectedFacility, alertFilter, currentUser);

  // Filter by search query
  const filteredAlerts = useMemo(() => {
    if (!searchQuery) return allAlerts;
    const query = searchQuery.toLowerCase();
    return allAlerts.filter(alert => 
      alert.resident?.toLowerCase().includes(query) ||
      alert.issue?.toLowerCase().includes(query) ||
      alert.medication?.toLowerCase().includes(query) ||
      alert.type?.toLowerCase().includes(query) ||
      alert.ward?.toLowerCase().includes(query)
    );
  }, [allAlerts, searchQuery]);

  const filters = [
    { id: 'all', label: 'All Alerts', icon: Bell, color: 'gray' },
    { id: 'critical', label: 'Critical', icon: AlertTriangle, color: 'red' },
    { id: 'safety', label: 'Safety', icon: Shield, color: 'orange' },
    { id: 'interaction', label: 'Interactions', icon: Zap, color: 'yellow' },
    { id: 'supply', label: 'Supply', icon: Package, color: 'blue' },
    { id: 'compliance', label: 'Compliance', icon: CheckCircle, color: 'green' },
    { id: 'incident', label: 'Incidents', icon: AlertCircle, color: 'purple' },
  ];

  // Calculate statistics
  const stats = useMemo(() => {
    const total = filteredAlerts.length;
    const critical = filteredAlerts.filter(a => a.priority === 'High').length;
    const active = filteredAlerts.filter(a => a.status === 'Active').length;
    const resolved = filteredAlerts.filter(a => a.status === 'Resolved').length;
    const automated = filteredAlerts.filter(a => a.automatedWorkflow).length;
    
    return { total, critical, active, resolved, automated };
  }, [filteredAlerts]);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'bg-red-100 text-red-800 border-red-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'critical': return AlertTriangle;
      case 'safety': return Shield;
      case 'interaction': return Zap;
      case 'supply': return Package;
      case 'compliance': return CheckCircle;
      case 'incident': return AlertCircle;
      default: return Bell;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'critical': return 'text-red-600 bg-red-50';
      case 'safety': return 'text-orange-600 bg-orange-50';
      case 'interaction': return 'text-yellow-600 bg-yellow-50';
      case 'supply': return 'text-blue-600 bg-blue-50';
      case 'compliance': return 'text-green-600 bg-green-50';
      case 'incident': return 'text-purple-600 bg-purple-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const handleViewAlert = (alert) => {
    setSelectedAlert(alert);
    setShowDetailModal(true);
  };

  const handleResolveAlert = (alertId) => {
    // In production, this would update the alert status
    console.log('Resolving alert:', alertId);
    showToast('Alert resolved (mock action)', 'info');
  };

  const handleExport = (format) => {
    const data = filteredAlerts.map(alert => ({
      ID: alert.id,
      Type: alert.type,
      Priority: alert.priority,
      Resident: alert.resident,
      Ward: alert.ward,
      Room: alert.room,
      Medication: alert.medication || 'N/A',
      Issue: alert.issue,
      Action: alert.action,
      Status: alert.status,
      Timestamp: alert.timestamp,
      Escalated: alert.escalated ? 'Yes' : 'No',
      Automated: alert.automatedWorkflow ? 'Yes' : 'No'
    }));

    if (format === 'csv') {
      const headers = Object.keys(data[0] || {}).join(',');
      const rows = data.map(row => Object.values(row).join(','));
      const csv = [headers, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `alerts-${selectedFacility}-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    } else if (format === 'json') {
      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `alerts-${selectedFacility}-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-red-600 via-orange-600 to-yellow-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
              <Bell className="w-8 h-8" />
              Alerts & Automation
            </h1>
            <p className="text-red-100">Real-time alerts, automated workflows, and safety monitoring</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleExport('csv')}
              className="bg-white bg-opacity-20 text-white px-4 py-2 rounded-xl hover:bg-opacity-30 transition-all flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <button
              onClick={() => handleExport('json')}
              className="bg-white bg-opacity-20 text-white px-4 py-2 rounded-xl hover:bg-opacity-30 transition-all flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export JSON
            </button>
            <button className="bg-white bg-opacity-20 text-white px-4 py-2 rounded-xl hover:bg-opacity-30 transition-all">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl p-5 shadow-lg border-2 border-gray-200 hover:shadow-xl transition-all">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Bell className="w-5 h-5 text-gray-600" />
            </div>
            <span className="text-xs text-gray-500">Total</span>
          </div>
          <div className="text-3xl font-bold text-gray-800">{stats.total}</div>
          <div className="text-sm text-gray-600 mt-1">All Alerts</div>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-lg border-2 border-red-200 hover:shadow-xl transition-all">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <span className="text-xs text-gray-500">Critical</span>
          </div>
          <div className="text-3xl font-bold text-red-600">{stats.critical}</div>
          <div className="text-sm text-gray-600 mt-1">High Priority</div>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-lg border-2 border-orange-200 hover:shadow-xl transition-all">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Activity className="w-5 h-5 text-orange-600" />
            </div>
            <span className="text-xs text-gray-500">Active</span>
          </div>
          <div className="text-3xl font-bold text-orange-600">{stats.active}</div>
          <div className="text-sm text-gray-600 mt-1">Require Action</div>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-lg border-2 border-green-200 hover:shadow-xl transition-all">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <span className="text-xs text-gray-500">Resolved</span>
          </div>
          <div className="text-3xl font-bold text-green-600">{stats.resolved}</div>
          <div className="text-sm text-gray-600 mt-1">Completed</div>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-lg border-2 border-blue-200 hover:shadow-xl transition-all">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Zap className="w-5 h-5 text-blue-600" />
            </div>
            <span className="text-xs text-gray-500">Automated</span>
          </div>
          <div className="text-3xl font-bold text-blue-600">{stats.automated}</div>
          <div className="text-sm text-gray-600 mt-1">Auto-Processed</div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            {filters.map((filter) => {
              const Icon = filter.icon;
              const isActive = alertFilter === filter.id;
              return (
                <button
                  key={filter.id}
                  onClick={() => actions.setAlertFilter(filter.id)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                    isActive
                      ? `bg-${filter.color}-100 text-${filter.color}-700 border-2 border-${filter.color}-300`
                      : 'bg-gray-50 text-gray-600 border-2 border-transparent hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {filter.label}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {viewMode === 'grid' ? <FileText className="w-4 h-4" /> : <Activity className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search alerts by resident, medication, issue, or type..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none transition-colors"
          />
        </div>
      </div>

      {/* Alerts Grid/List */}
      {filteredAlerts.length === 0 ? (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-12 text-center">
          <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No Alerts Found</h3>
          <p className="text-gray-500">No alerts match your current filters and search criteria.</p>
        </div>
      ) : (
        <div className={viewMode === 'grid' 
          ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          : "space-y-4"
        }>
          {filteredAlerts.map((alert) => {
            const TypeIcon = getTypeIcon(alert.type);
            const priorityColor = getPriorityColor(alert.priority);
            const typeColor = getTypeColor(alert.type);
            
            return (
              <div
                key={alert.id}
                className={`bg-white rounded-xl shadow-lg border-2 ${
                  alert.priority === 'High' ? 'border-red-200' :
                  alert.priority === 'Medium' ? 'border-yellow-200' :
                  'border-green-200'
                } hover:shadow-xl transition-all cursor-pointer overflow-hidden relative`}
                onClick={() => handleViewAlert(alert)}
              >
                {/* Priority Indicator Bar */}
                <div className={`h-1 ${
                  alert.priority === 'High' ? 'bg-red-500' :
                  alert.priority === 'Medium' ? 'bg-yellow-500' :
                  'bg-green-500'
                }`}></div>

                <div className="p-5">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${typeColor}`}>
                        <TypeIcon className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-800 capitalize">{alert.type}</span>
                          {alert.escalated && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-medium">
                              Escalated
                            </span>
                          )}
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${priorityColor} border`}>
                          {alert.priority}
                        </span>
                      </div>
                    </div>
                    {alert.status === 'Active' && (
                      <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                    )}
                  </div>

                  {/* Alert Content */}
                  <div className="space-y-3">
                    <div>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                        <User className="w-4 h-4" />
                        <span className="font-medium">{alert.resident}</span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {alert.ward} • {alert.room}
                        </span>
                      </div>
                    </div>

                    {alert.medication && (
                      <div className="flex items-center gap-2 text-sm">
                        <Pill className="w-4 h-4 text-purple-600" />
                        <span className="font-medium text-gray-800">{alert.medication}</span>
                      </div>
                    )}

                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm font-semibold text-gray-700 mb-1">Issue:</p>
                      <p className="text-sm text-gray-600">{alert.issue}</p>
                    </div>

                    {alert.action && (
                      <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                        <p className="text-sm font-semibold text-blue-700 mb-1">Required Action:</p>
                        <p className="text-sm text-blue-600">{alert.action}</p>
                      </div>
                    )}

                    {/* Automated Workflow Status */}
                    {alert.automatedWorkflow && (
                      <div className="flex items-center gap-2 bg-green-50 rounded-lg p-2 border border-green-200">
                        <Zap className="w-4 h-4 text-green-600" />
                        <span className="text-xs text-green-700 font-medium">{alert.workflowStatus}</span>
                      </div>
                    )}

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Clock className="w-3 h-3" />
                        <span>{formatDate(alert.timestamp)} {formatTime(alert.timestamp)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          alert.status === 'Active' ? 'bg-orange-100 text-orange-700' :
                          alert.status === 'Resolved' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {alert.status}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResolveAlert(alert.id);
                          }}
                          className="p-1.5 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors"
                          title="Resolve Alert"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Alert Detail Modal */}
      {showDetailModal && selectedAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className={`bg-gradient-to-r ${
              selectedAlert.priority === 'High' ? 'from-red-600 to-red-700' :
              selectedAlert.priority === 'Medium' ? 'from-yellow-600 to-yellow-700' :
              'from-green-600 to-green-700'
            } text-white p-6 rounded-t-2xl`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {(() => {
                    const TypeIcon = getTypeIcon(selectedAlert.type);
                    return <TypeIcon className="w-8 h-8" />;
                  })()}
                  <div>
                    <h2 className="text-2xl font-bold capitalize">{selectedAlert.type} Alert</h2>
                    <p className="text-red-100">Alert ID: {selectedAlert.id}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="p-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Priority & Status */}
              <div className="flex items-center gap-4">
                <span className={`px-4 py-2 rounded-lg font-bold ${getPriorityColor(selectedAlert.priority)}`}>
                  {selectedAlert.priority} Priority
                </span>
                <span className={`px-4 py-2 rounded-lg font-medium ${
                  selectedAlert.status === 'Active' ? 'bg-orange-100 text-orange-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {selectedAlert.status}
                </span>
                {selectedAlert.escalated && (
                  <span className="px-4 py-2 rounded-lg font-medium bg-red-100 text-red-700">
                    Escalated
                  </span>
                )}
              </div>

              {/* Resident Information */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-semibold text-gray-800 mb-3">Resident Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Resident</p>
                    <p className="font-medium text-gray-800">{selectedAlert.resident}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Location</p>
                    <p className="font-medium text-gray-800">{selectedAlert.ward} • {selectedAlert.room}</p>
                  </div>
                </div>
              </div>

              {/* Alert Details */}
              <div className="space-y-4">
                {selectedAlert.medication && (
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-1">Medication</p>
                    <p className="text-gray-800">{selectedAlert.medication}</p>
                  </div>
                )}

                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">Issue</p>
                  <p className="text-gray-800 bg-red-50 p-3 rounded-lg border border-red-200">{selectedAlert.issue}</p>
                </div>

                {selectedAlert.action && (
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-1">Required Action</p>
                    <p className="text-gray-800 bg-blue-50 p-3 rounded-lg border border-blue-200">{selectedAlert.action}</p>
                  </div>
                )}

                {selectedAlert.automatedWorkflow && (
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="w-5 h-5 text-green-600" />
                      <p className="font-semibold text-green-800">Automated Workflow</p>
                    </div>
                    <p className="text-sm text-green-700">{selectedAlert.workflowStatus}</p>
                  </div>
                )}
              </div>

              {/* Timestamp */}
              <div className="flex items-center gap-2 text-sm text-gray-600 pt-4 border-t border-gray-200">
                <Clock className="w-4 h-4" />
                <span>Created: {formatDate(selectedAlert.timestamp)} at {formatTime(selectedAlert.timestamp)}</span>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => handleResolveAlert(selectedAlert.id)}
                  className="flex-1 bg-green-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                >
                  <CheckCircle className="w-5 h-5" />
                  Resolve Alert
                </button>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Alerts;
